import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, Modal, IconButton } from "@material-ui/core";
import { HelpCircle, Play, X, Search } from "lucide-react";

import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      display: "flex",
      flexDirection: "column",
      ...(theme.scrollbarStyles || {}),
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    countChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: 16,
      padding: "4px 4px 16px",
    },
    card: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      borderRadius: 12,
      overflow: "hidden",
      cursor: "pointer",
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#ffffff"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      transition:
        "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: theme.palette.primary.main,
        boxShadow: isDark
          ? "0 8px 24px rgba(0,0,0,0.45)"
          : "0 8px 24px rgba(15,23,42,0.12)",
        "& $playOverlay": {
          opacity: 1,
        },
        "& $thumbnail": {
          transform: "scale(1.04)",
        },
      },
    },
    thumbnailWrap: {
      position: "relative",
      width: "100%",
      paddingTop: "56.25%",
      overflow: "hidden",
      background: isDark ? "#0a0d12" : "#f0f2f5",
    },
    thumbnail: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transition: "transform 0.35s ease",
    },
    playOverlay: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background:
        "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.45) 100%)",
      opacity: 0,
      transition: "opacity 0.22s ease",
    },
    playButton: {
      width: 56,
      height: 56,
      borderRadius: "50%",
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText || "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
    },
    cardBody: {
      padding: "12px 14px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    cardTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      lineHeight: 1.35,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    cardDescription: {
      fontSize: 12.5,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      lineHeight: 1.5,
      display: "-webkit-box",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    skeletonCard: {
      borderRadius: 12,
      overflow: "hidden",
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#ffffff"),
      border: `1px solid ${
        dsColors.border || (isDark ? "#202632" : "#e8e8e8")
      }`,
      display: "flex",
      flexDirection: "column",
    },
    skeletonThumb: {
      width: "100%",
      paddingTop: "56.25%",
      background: isDark
        ? "linear-gradient(90deg, #14181f 0%, #1a1f29 50%, #14181f 100%)"
        : "linear-gradient(90deg, #eceff3 0%, #f5f6f9 50%, #eceff3 100%)",
      backgroundSize: "200% 100%",
      animation: "$shimmer 1.4s linear infinite",
    },
    skeletonLine: {
      height: 12,
      borderRadius: 6,
      margin: "8px 14px",
      background: isDark
        ? "linear-gradient(90deg, #14181f 0%, #1a1f29 50%, #14181f 100%)"
        : "linear-gradient(90deg, #eceff3 0%, #f5f6f9 50%, #eceff3 100%)",
      backgroundSize: "200% 100%",
      animation: "$shimmer 1.4s linear infinite",
    },
    "@keyframes shimmer": {
      "0%": { backgroundPosition: "200% 0" },
      "100%": { backgroundPosition: "-200% 0" },
    },
    emptyState: {
      gridColumn: "1 / -1",
      padding: "60px 16px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      textAlign: "center",
    },
    emptyIcon: {
      width: 48,
      height: 48,
      borderRadius: 12,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    emptyTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    videoModal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    },
    videoModalContent: {
      outline: "none",
      width: "100%",
      maxWidth: 1024,
      aspectRatio: "16/9",
      position: "relative",
      backgroundColor: "#000",
      borderRadius: 12,
      overflow: "hidden",
      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    },
    closeBtn: {
      position: "absolute",
      top: 8,
      right: 8,
      zIndex: 2,
      width: 36,
      height: 36,
      borderRadius: 10,
      background: "rgba(0,0,0,0.55)",
      color: "#fff",
      "&:hover": {
        background: "rgba(0,0,0,0.75)",
      },
    },
  };
});

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const helps = await list();
        if (active) setRecords(helps || []);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = (video) => setSelectedVideo(video);
  const closeVideoModal = () => setSelectedVideo(null);

  const handleModalClose = useCallback((event) => {
    if (event.key === "Escape") closeVideoModal();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleModalClose);
    return () => document.removeEventListener("keydown", handleModalClose);
  }, [handleModalClose]);

  const renderSkeletons = () =>
    Array.from({ length: 6 }).map((_, i) => (
      <div key={`sk-${i}`} className={classes.skeletonCard}>
        <div className={classes.skeletonThumb} />
        <div className={classes.skeletonLine} style={{ width: "70%" }} />
        <div className={classes.skeletonLine} style={{ width: "90%" }} />
        <div
          className={classes.skeletonLine}
          style={{ width: "50%", marginBottom: 14 }}
        />
      </div>
    ));

  const renderEmpty = () => (
    <div className={classes.emptyState}>
      <span className={classes.emptyIcon}>
        <Search size={22} strokeWidth={1.8} />
      </span>
      <span className={classes.emptyTitle}>
        {i18n.t("helps.empty.title", { defaultValue: "Sem vídeos disponíveis" })}
      </span>
      <span style={{ fontSize: 12.5 }}>
        {i18n.t("helps.empty.subtitle", {
          defaultValue: "Os vídeos publicados aparecerão aqui.",
        })}
      </span>
    </div>
  );

  return (
    <div className={classes.container}>
      <div className={classes.pageHeader}>
        <div className={classes.headerLeft}>
          <span className={classes.pageIconWrap}>
            <HelpCircle size={20} strokeWidth={1.8} />
          </span>
          <div>
            <div className={classes.pageTitle}>
              {i18n.t("helps.title")}
              <span className={classes.countChip}>{records.length}</span>
            </div>
            <div className={classes.pageSubtitle}>
              {i18n.t("helps.subtitle", {
                defaultValue:
                  "Tutoriais em vídeo para tirar o máximo da plataforma.",
              })}
            </div>
          </div>
        </div>
      </div>

      <div className={classes.grid}>
        {loading
          ? renderSkeletons()
          : records.length === 0
          ? renderEmpty()
          : records.map((record, key) => (
              <div
                key={key}
                className={classes.card}
                onClick={() => openVideoModal(record.video)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    openVideoModal(record.video);
                  }
                }}
              >
                <div className={classes.thumbnailWrap}>
                  <img
                    src={`https://img.youtube.com/vi/${record.video}/mqdefault.jpg`}
                    alt={i18n.t("helps.thumbnail")}
                    className={classes.thumbnail}
                    loading="lazy"
                  />
                  <div className={classes.playOverlay}>
                    <span className={classes.playButton}>
                      <Play size={22} strokeWidth={2} fill="currentColor" />
                    </span>
                  </div>
                </div>
                <div className={classes.cardBody}>
                  <div className={classes.cardTitle}>{record.title}</div>
                  {record.description && (
                    <div className={classes.cardDescription}>
                      {record.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
      </div>

      <Modal
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        className={classes.videoModal}
      >
        <div className={classes.videoModalContent}>
          <IconButton
            className={classes.closeBtn}
            onClick={closeVideoModal}
            aria-label="close"
            size="small"
          >
            <X size={18} strokeWidth={2} />
          </IconButton>
          {selectedVideo && (
            <iframe
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                border: 0,
              }}
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title={i18n.t("helps.videoPlayerTitle")}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Helps;
